import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Globe, Mail, Phone, Briefcase, ArrowLeft, User, BadgeCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Spinner, DemoBadge } from "@/components/common";
import { ReportDialog } from "@/components/ReportDialog";
import { useAuth } from "@/context/AuthContext";

export default function BusinessDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const authed = user && typeof user === "object";
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimStatus, setClaimStatus] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/businesses/${id}`).then(({ data }) => setBiz(data)).catch(() => setBiz(false)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (authed && biz && biz.claimed === false) {
      api.get(`/businesses/${id}/my-claim`).then(({ data }) => setClaimStatus(data.status)).catch(() => {});
    }
  }, [authed, biz, id]);

  const claim = async () => {
    if (!authed) { navigate("/login"); return; }
    setClaiming(true);
    try {
      const { data } = await api.post(`/businesses/${id}/claim`);
      setClaimStatus(data.status || "pending");
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClaiming(false);
    }
  };

  if (loading) return <div className="container-nx section-nx"><Spinner /></div>;
  if (!biz) return (
    <div className="container-nx section-nx text-center">
      <p className="text-slate-500">Business not found.</p>
      <Link to="/businesses" className="btn-outline-navy mt-4 inline-flex">Back to directory</Link>
    </div>
  );

  return (
    <div className="container-nx section-nx max-w-4xl">
      <Link to="/businesses" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B192C]" data-testid="back-to-businesses">
        <ArrowLeft className="h-4 w-4" /> Back to directory
      </Link>

      <div className="card-nx overflow-hidden">
        <div className="h-48 w-full overflow-hidden bg-[#F5F3EF]">
          {biz.logo_url ? (
            <img src={biz.logo_url} alt={biz.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center"><Briefcase className="h-14 w-14 text-slate-300" /></div>
          )}
        </div>
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-[#0B192C] font-head">{biz.name}</h1>
            {biz.is_demo && <DemoBadge />}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="chip-gold">{biz.industry}</span>
            <span className="chip"><MapPin className="mr-1 h-3 w-3" />{biz.city}</span>
            {biz.service_area && <span className="chip">Serves: {biz.service_area}</span>}
          </div>

          {biz.description && <p className="mt-6 text-sm leading-relaxed text-slate-600">{biz.description}</p>}

          {biz.products?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Products & Services</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {biz.products.map((p) => <span key={p} className="chip">{p}</span>)}
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-6 text-sm">
            {biz.website && (
              <a href={biz.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#B8860B] hover:underline" data-testid="business-website">
                <Globe className="h-4 w-4" /> {biz.website}
              </a>
            )}
            {biz.contact_email && (
              <a href={`mailto:${biz.contact_email}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0B192C]">
                <Mail className="h-4 w-4" /> {biz.contact_email}
              </a>
            )}
            {biz.contact_phone && (
              <span className="inline-flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4" /> {biz.contact_phone}</span>
            )}
          </div>

          {biz.claimed === false ? (
            <div className="mt-6 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4" data-testid="claim-panel">
              <p className="text-sm font-semibold text-[#8a6d12]">
                Community listing{biz.representative_name ? ` · ${biz.representative_name}` : ""}
              </p>
              {claimStatus === "pending" ? (
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700" data-testid="claim-pending">
                  <Clock className="h-3.5 w-3.5" /> Your claim is pending admin approval
                </p>
              ) : (
                <>
                  <p className="mt-1 text-xs text-slate-500">
                    This business was added by the community team. Is it yours? Claim it — an admin will review and approve your request before ownership transfers.
                  </p>
                  {claimStatus === "rejected" && (
                    <p className="mt-2 text-xs font-medium text-red-600" data-testid="claim-rejected">
                      A previous claim was not approved. You may submit a new request.
                    </p>
                  )}
                  <button onClick={claim} disabled={claiming} className="btn-gold mt-3 px-4 py-2 text-xs" data-testid="claim-business-btn">
                    <BadgeCheck className="h-4 w-4" /> {claiming ? "Submitting..." : authed ? "Request to claim" : "Login to claim"}
                  </button>
                </>
              )}
            </div>
          ) : biz.owner ? (
            <Link to={`/members/${biz.owner.id}`} className="mt-6 flex items-center gap-3 rounded-lg bg-[#F5F3EF] p-4 transition hover:bg-[#ece8e0]" data-testid="business-owner-link">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2563EB] text-white"><User className="h-5 w-5" /></div>
              <div>
                <p className="text-xs text-slate-400">Represented by</p>
                <p className="text-sm font-semibold text-[#0B192C]">{biz.owner.name}</p>
              </div>
            </Link>
          ) : null}

          <div className="mt-6 border-t border-slate-100 pt-4">
            <ReportDialog targetType="business" targetId={biz.id} label="Report this business" />
          </div>
        </div>
      </div>
    </div>
  );
}
